
import { Component, Prop, State, Vue } from 'nuxt-property-decorator';
import BaseMediaGallery from 'nuxt-typo3/lib/templates/components/content/elements/media/Gallery';
import type { PropType } from 'vue';
import ImageCarousel from './ImageCarousel.vue';
import MediaFile from './MediaFile.vue';
import { ColPosAppearance, Gallery, RootPage } from '@/types/pageContract';
import { File, ImageMetaData } from '@/types/contentContract';
import LocalConfigStore from '@/store/localConfig.store';

@Component({
  components: { ImageCarousel, MediaFile },
  extends: BaseMediaGallery
})
export default class MediaGallery extends Vue implements BaseMediaGallery {
  // @ts-ignore
  @State((state) => state.typo3?.page)
  rootPage!: RootPage;

  @Prop({ type: Boolean, default: false })
  useImageCarousel!: boolean;

  @Prop({ type: Object as PropType<ImageMetaData>, default: () => {} })
  imageMetaData!: ImageMetaData;

  @Prop({ type: Object as PropType<ColPosAppearance>, default: () => {} })
  appearance!: ColPosAppearance;

  maxGalleryWidth: number = 325; // this is the same value as minWInText from typo3. Should we have this in the api settings?
  minWidthSize: number = 100;
  spacing: number = 24;
  gallery!: Gallery;
  isCarousel: boolean = false;
  imageWidth: number = 0;

  /*
  The MediaLabels in the backend allows you to enter one label per image, separated by newlines
  */
  get mediaLabels(): Array<string> {
    return this.imageMetaData.medialabels
      ? this.imageMetaData.medialabels.split('\r\n')
      : [''];
  }

  get hasImageMetaData(): boolean {
    return (
      this.imageMetaData.imagecaption !== '' ||
      this.imageMetaData.medialabels !== ''
    );
  }

  get carouselImages(): Array<File> {
    const rows = this.gallery.rows;
    const carouselImages = [];

    Object.values(rows).forEach((row: any) => {
      Object.values(row).forEach((columns: any) => {
        Object.values(columns).forEach((column: File) => {
          carouselImages.push(column);
        });
      });
    });

    return carouselImages;
  }

  get wideOrFullScreenImage(): boolean {
    return (
      (this.appearance && this.appearance?.layout.includes('fullscreen')) ||
      (this.appearance && this.appearance?.layout.includes('widescreen'))
    );
  }

  get widthSize(): string {
    // If gallery width is between 1 - 325 (half size of grid), we force the size to minimum 175
    return this.gallery.width > 0 &&
      this.gallery.width < this.minWidthSize &&
      this.gallery.width < this.maxGalleryWidth
      ? `${this.minWidthSize}px`
      : `${this.gallery.width}px`;
  }

  get isFrontPage(): boolean {
    return LocalConfigStore.isFrontPage;
  }

  get handleMediaSizeAndSpacing(): object {
    return {
      ...this.maxOrMinWidth,
      margin:
        this.gallery.position.horizontal === 'center' && !this.isFrontPage
          ? '0 auto'
          : ''
    };
  }

  get maxOrMinWidth(): object {
    if (this.useImageCarousel) {
      return {};
    }
    if (this.wideOrFullScreenImage || this.gallery.equalMediaHeight) {
      if (this.gallery.position.vertical === 'intext') {
        return { minWidth: this.widthSize, maxWidth: this.widthSize };
      }

      if (this.isGalleryInBlock) return { maxWidth: '100%!important' };

      return { maxWidth: 'none' };
    }

    if (
      this.gallery.position.vertical === 'intext' &&
      this.gallery.position.noWrap
    ) {
      return { minWidth: this.widthSize, maxWidth: this.widthSize };
    }

    return { maxWidth: this.widthSize };
  }

  get isGalleryInBlock(): boolean {
    return this.appearance?.extendedcmsElementType !== '';
  }

  get width(): object {
    let width = `auto`;
    if (this.$refs.imageList) {
      width = `${this.$refs.imageList[0].clientWidth}px`;
    }
    if (this.useImageCarousel === false) {
      return {
        width
      };
    }
  }

  mounted() {
    if (this.useImageCarousel === false && this.$refs.imageList) {
      this.isCarousel = false;
      this.imageWidth = this.$refs.imageList[0].clientWidth;
    }
    if (this.useImageCarousel) {
      this.isCarousel = true;
    }
  }

  gridWidth(col: File, count: number): object {
    if (!col) {
      return {};
    }

    return {
      maxWidth: this.desktopImageMaxWidth(col),
      flex: `0 1 ${this.colGridWidth(count)}`
    };
  }

  private desktopImageMaxWidth(col: File): string {
    let desktopFile: File;
    if (this.gallery.responsive) {
      desktopFile =
        this.gallery.responsive.desktop[
          col.properties.fileReferenceUid as string
        ];
    }

    // Only handle svg edge cases for now, to prevent errors on other images
    if (col && col.publicUrl && col.publicUrl.includes('.svg')) {
      if (this.gallery.equalMediaHeight) {
        return `${col.properties.dimensions.width}px`;
      }

      return '100%';
    }

    if (
      this.appearance?.layout.includes('fullscreen') ||
      this.appearance?.layout.includes('widescreen')
    ) {
      desktopFile =
        this.gallery.responsive.wide[col.properties.fileReferenceUid as string];
      return `${desktopFile.properties.dimensions.width}px`;
    }

    if (!col || desktopFile === undefined) {
      return '';
    }

    if (this.$vuetify.breakpoint.smAndDown) {
      return '100%';
    }

    if (!col || this.wideOrFullScreenImage) {
      return '100%';
    }

    if (this.gallery.equalMediaHeight) {
      return `${desktopFile.properties.dimensions.width}px`;
    }

    if (this.gallery.count.columns > 1) {
      return `${
        desktopFile.properties.dimensions.width + this.gallery.columnSpacing
      }px`;
    }

    return `${desktopFile.properties.dimensions.width}px`;
  }

  private colGridWidth(count: number): string {
    if (this.gallery.equalMediaHeight) {
      return 'auto';
    }

    if (this.$vuetify.breakpoint.mdAndUp) {
      return `${100 / count}%`;
    }

    return '100%';
  }
}
